// DrawerCreateClientForm — slim「新增終端客戶」form for the AI 導覽
// drawer. Drives the same /v1/clients POST as /dashboard/clients's
// inline drawer form but without the edit-mode chrome.
//
// AM-7b spec docs/specs/ai-mode-operations-console.md §5.2.

import React, { useState } from "react";
import { createClient, friendlyClientsError } from "../lib/clientsApi.js";

interface Props {
  prefill: Record<string, unknown>;
  tenantId: string | null;
  onSaved: (entityId: string) => void;
  onCancel: () => void;
}

function readStringPrefill(prefill: Record<string, unknown>, key: string): string {
  const v = prefill[key];
  return typeof v === "string" ? v : "";
}

export default function DrawerCreateClientForm({
  prefill, tenantId, onSaved, onCancel,
}: Props): React.ReactElement {
  const [name, setName] = useState(readStringPrefill(prefill, "name"));
  const [contactName, setContactName] = useState(readStringPrefill(prefill, "contact_name"));
  const [contactEmail, setContactEmail] = useState(readStringPrefill(prefill, "contact_email"));
  const [contactPhone, setContactPhone] = useState(readStringPrefill(prefill, "contact_phone"));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await createClient({
        name: name.trim(),
        contact_name: contactName.trim() || null,
        contact_email: contactEmail.trim() || null,
        contact_phone: contactPhone.trim() || null,
        status: "active",
      }, tenantId);
      onSaved(r.id);
    } catch (e) {
      setErr(friendlyClientsError(e));
    } finally {
      setBusy(false);
    }
  };

  const canSave = name.trim().length > 0 && !busy;

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-text-secondary mb-1">客戶名稱 *</label>
        <input
          className="input w-full"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="例：達飛安控 信義店"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-text-secondary mb-1">聯絡人姓名</label>
        <input
          className="input w-full"
          value={contactName}
          onChange={(e) => setContactName(e.target.value)}
          placeholder="例：王主任"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-text-secondary mb-1">聯絡 Email</label>
        <input
          type="email"
          className="input w-full"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          placeholder="例：contact@example.com"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-text-secondary mb-1">聯絡電話</label>
        <input
          type="tel"
          className="input w-full"
          value={contactPhone}
          onChange={(e) => setContactPhone(e.target.value)}
          placeholder="例：02-1234-5678"
        />
      </div>
      {err && (
        <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          {err}
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} disabled={busy} className="btn-secondary text-sm">取消</button>
        <button type="button" onClick={() => void save()} disabled={!canSave} className="btn-primary text-sm">
          {busy ? "儲存中…" : "新增"}
        </button>
      </div>
    </div>
  );
}
